import { useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, Loader2, ExternalLink, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfidenceBadge } from "@/components/app/Research";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/ui";

const key = (c) => `${c.kind}:${c.id}`;

const OfficialSources = ({ list, value, onChange }) => (
  <div className="space-y-1" data-testid="deadline-official-sources">
    <div className="text-xs font-semibold">1. Open and check the official source</div>
    {list.map((s) => (
      <label key={s.url} className="flex items-start gap-2 text-xs cursor-pointer" data-testid={`deadline-source-${list.indexOf(s)}`}>
        <input type="radio" name="official" className="mt-0.5" checked={value === s.url} onChange={() => onChange(s.url)} />
        <span><a href={s.url} target="_blank" rel="noreferrer noopener" className="text-primary hover:underline inline-flex items-center gap-1 break-all">{s.title}<ExternalLink className="h-3 w-3 shrink-0" /></a>
          <span className="text-emerald-600 dark:text-emerald-400"> · Government / regulator</span></span>
      </label>))}
  </div>
);

const Candidates = ({ p, picked, toggle, date }) => (
  <div>
    <div className="text-xs font-semibold mb-1">3. Choose the deadlines to change</div>
    <div className="rounded-lg border border-border divide-y divide-border">{p.candidates.map((c) => (
      <label key={key(c)} className="flex flex-wrap items-center gap-3 p-2.5 cursor-pointer text-sm" data-testid={`deadline-candidate-${c.id}`}>
        <Checkbox checked={picked.has(key(c))} onCheckedChange={() => toggle(key(c))} />
        <span className="flex-1 min-w-[140px]">{c.title} <span className="text-muted-foreground">· {c.entity_name}</span></span>
        <span className="font-mono text-xs">{fmtDate(c.current_due)} → {date ? fmtDate(date) : "?"}</span>
      </label>))}</div>
  </div>
);

const Form = ({ p, alertId, onClose, done }) => {
  const [picked, setPicked] = useState(new Set());
  const [src, setSrc] = useState("");
  const [date, setDate] = useState("");
  const [ok, setOk] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (k) => setPicked((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const differs = date && date !== p.deadline.new_due_date;
  const apply = async () => {
    setBusy(true);
    try {
      const items = p.candidates.filter((c) => picked.has(key(c))).map((c) => ({ kind: c.kind, id: c.id }));
      const r = (await api.post(`/regwatch/alerts/${alertId}/apply-deadline`, { items, confirm: true, new_due_date: date, verified_source_url: src, note })).data;
      toast.success(`${r.updated} deadline(s) updated · logged in the audit trail`);
      done?.();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-wrap items-center gap-2"><b>{p.title}</b><ConfidenceBadge c={p.confidence} testId="deadline-confidence" /></div>
      <p className="text-muted-foreground">{p.what_changed}</p>
      <p className="text-xs flex gap-1.5 rounded-md bg-amber-500/10 px-2 py-1.5"><ShieldAlert className="h-3.5 w-3.5 shrink-0 text-amber-500" />AI-suggested date: {fmtDate(p.deadline.new_due_date)} for {p.deadline.applies_to}. Don't rely on it. Check the official source and type the date yourself.</p>
      <OfficialSources list={p.official_sources} value={src} onChange={setSrc} />
      <div><div className="text-xs font-semibold mb-1">2. Type the new due date shown on that source</div>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="max-w-[200px]" data-testid="deadline-date-input" />
        {differs && <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1" data-testid="deadline-date-differs">This differs from the AI's suggestion. Your date will be used and both are recorded.</p>}</div>
      <Candidates p={p} picked={picked} toggle={toggle} date={date} />
      <Input placeholder="Note for the audit trail (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="deadline-note" />
      <label className="flex items-start gap-2 text-xs"><Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="deadline-confirm-check" />I checked the official source and confirm these due dates should change. The change is recorded in the audit trail and can be reverted.</label>
      <Button onClick={apply} disabled={busy || !ok || !picked.size || !src || !date} data-testid="deadline-apply-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CalendarCheck className="h-4 w-4 mr-1.5" />}Update {picked.size || ""} deadline(s)</Button>
    </div>
  );
};

export const DeadlineUpdateDialog = ({ alertId, onClose, done }) => {
  const state = useLoad(() => api.get(`/regwatch/alerts/${alertId}/deadline-plan`).then((r) => r.data));
  const p = state.data;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="deadline-dialog">
        <DialogHeader><DialogTitle>Review Filing Calendar update</DialogTitle></DialogHeader>
        {!p ? <Loader2 className="h-5 w-5 animate-spin" /> : !p.ready ? <p className="text-sm text-muted-foreground" data-testid="deadline-not-ready">{p.reason}</p>
          : !p.candidates.length ? <p className="text-sm text-muted-foreground" data-testid="deadline-no-match">None of your open deadlines match this change, so nothing needs updating.</p>
          : <Form p={p} alertId={alertId} onClose={onClose} done={done} />}
      </DialogContent>
    </Dialog>
  );
};
